import {
  Directive,
  HostBinding,
  Input,
  OnInit,
  OnChanges,
  AfterViewInit,
  ChangeDetectorRef,
  ElementRef,
  Renderer2,
  ViewContainerRef,
  ComponentFactoryResolver,
  SimpleChanges
} from '@angular/core';
import { Loading } from '../loading/loading.module';
import { ButtonType } from './button-types';


/**
 * Buttons are used primarily for actions, such as âAddâ, âCloseâ, âCancelâ, or âSaveâ.
 * Plain buttons, which look similar to links, are used for less important or less commonly used actions, such as âview shipping settingsâ.
 *
 * For navigational actions that appear within or directly following a sentence, use the link component.
 */
@Directive({
  selector: '[sukaButton]'
})
export class Button implements OnInit, AfterViewInit, OnChanges {
  /**
   * Sets the button type. Can be `primary`, `basic`, `plain`, `destructive` or `outline_white`.
   */
  @Input() sukaButton: ButtonType = 'basic';

  /**
   * Button size. Can be `md` (Medium), `sm` (Small) or `lg` (Large).
   */
  @Input() size?: 'md' | 'sm' | 'lg' = 'md';

  @HostBinding('class.btn--basic') get basic() {
    return this.getType('basic');
  }
  @HostBinding('class.btn--primary') get primary() {
    return this.getType('primary');
  }
  @HostBinding('class.btn--plain') get plain() {
    return this.getType('plain');
  }
  @HostBinding('class.btn--destructive') get destructive() {
    return this.getType('destructive');
  }
  @HostBinding('class.btn--outline_white') get outline_white() {
    return this.getType('outline_white');
  }

  /**
   * Set to `true` for a skeleton state button.
   */
  @HostBinding('class.skeleton') @Input() skeleton ? = false;
  /**
   * Set to `true` to display the outline version of the button.
   */
  @HostBinding('class.btn--outline') @Input() outline ? = false;
  /**
   * Set to `true` for a hover effect.
   */
  @HostBinding('class.btn--hover') @Input() hover ? = false;
  /**
   * Set to `true` for a button that uses the full horizontal width;
   */
  @HostBinding('class.btn--full_width') @Input() fullWidth ? = false;
  /**
   * Sets to `true` when the button is on a button group.
   */
  @HostBinding('class.btn--group_item') @Input() inGroup ? = false;
  /**
   * Set to `true` for a loading button.
   */
  @HostBinding('class.btn--loading') @Input() loading ? = false;

  @HostBinding('class.btn--sm') get buttonSmall() {
    return this.size === 'sm';
  }
  @HostBinding('class.btn--lg') get buttonLarge() {
    return this.size === 'lg';
  }

  /**
   * @ignore
   */
  loadingSpinner: Loading;

  constructor(
    private ref: ChangeDetectorRef,
    private el: ElementRef,
    private renderer: Renderer2,
    private viewContainerRef: ViewContainerRef,
    private componentFactoryResolver: ComponentFactoryResolver
  ) {}

  private getType(type: ButtonType): boolean {
    return this.sukaButton === type;
  }

  public ngOnInit() {
    if (!this.sukaButton) {
      this.sukaButton = 'basic';
    }

    // Create the spinner
    const factory = this.componentFactoryResolver.resolveComponentFactory(Loading);
    const componentRef = this.viewContainerRef.createComponent(factory);
    this.loadingSpinner = componentRef.instance;

    this.loadingSpinner.size = 'sm';

    this.renderer.setStyle(this.loadingSpinner.elementRef.nativeElement, 'display', 'none');

    this.renderer.appendChild(this.el.nativeElement, this.loadingSpinner.elementRef.nativeElement);

    if (this.loading) {
      this.renderer.setStyle(this.loadingSpinner.elementRef.nativeElement, 'display', 'inherit');
      this.el.nativeElement.disabled = true;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.loading && this.loadingSpinner) {
      if (this.loading) {
        this.renderer.setStyle(this.loadingSpinner.elementRef.nativeElement, 'display', 'inherit');
      } else {
        this.renderer.setStyle(this.loadingSpinner.elementRef.nativeElement, 'display', 'none');
      }

      this.el.nativeElement.disabled = changes.loading.currentValue;
    }
  }

  ngAfterViewInit() {
    this.ref.detectChanges();
  }
}
